import React from "react";
import axios from "axios";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";

/* ===================== helpers ===================== */

const DAY_CODES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const toMin = (hhmm) => {
  const [H, M] = String(hhmm || "00:00").split(":").map(Number);
  return (Number.isFinite(H) ? H : 0) * 60 + (Number.isFinite(M) ? M : 0);
};
const toHHMM = (min) => {
  const H = Math.floor(min / 60);
  const M = min % 60;
  return `${String(H).padStart(2, "0")}:${String(M).padStart(2, "0")}`;
};
const minutesBetween = (minutesMap, fromKey, toKey) => {
  if (!fromKey || !toKey) return null;
  const v = minutesMap.get(`${fromKey}|${toKey}`);
  return Number.isFinite(v) ? v : null;
};

const lookupMinutes = (minutesMap, fromKey, toKey) => {
  const d1 = minutesBetween(minutesMap, fromKey, toKey);
  if (Number.isFinite(d1)) return d1;
  const d2 = minutesBetween(minutesMap, toKey, fromKey);
  return Number.isFinite(d2) ? d2 : null;
};

/* ===================== data hook ===================== */
function useTeacherNetwork({ teacherId, schoolId, bucketHHMM }) {
  const [endpoints, setEndpoints] = React.useState([]);
  const [minutesMap, setMinutesMap] = React.useState(new Map());
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    if (!teacherId) return;
    setLoading(true);
    setErr(null);
    const url = `${baseUrl}/teacher-scheduling/${teacherId}`;

    axios
      .get(url, getRequestHeaders())
      .then((res) => {
        console.log(res.data)
        const data = res.data || {};
        const eps = Array.isArray(data.endpoints) ? data.endpoints : [];
        const edges = Array.isArray(data.edges) ? data.edges : [];

        const map = new Map();
        for (const e of edges) {
          const fromKey =
            (typeof e.from === "string" ? e.from : e.from?.key) ??
            e.origin?.key ??
            e.originKey ??
            null;

          const toKey =
            (typeof e.to === "string" ? e.to : e.to?.key) ??
            e.destination?.key ??
            e.destinationKey ??
            null;

          const minutes = Number(e.minutes ?? e.min ?? e.duration ?? e.durationMin);
          if (fromKey && toKey && Number.isFinite(minutes)) {
            map.set(`${fromKey}|${toKey}`, minutes);
          }
        }

        setEndpoints(eps);
        setMinutesMap(map);
      })
      .catch((e) => setErr(e?.message || "Failed to load teacher network"))
      .finally(() => setLoading(false));
  }, [teacherId, schoolId, bucketHHMM]);

  const progress = { done: minutesMap.size, total: endpoints.length * Math.max(0, endpoints.length - 1) };
  return { endpoints, minutesMap, loading, err, progress };
}

/* ===================== Interactive Optimizer (DnD) ===================== */

function InteractiveOptimizer({ teacher, endpoints, minutesMap }) {
  // home = teacher.Name match; fallback first endpoint
  const homeEp = React.useMemo(() => {
    if (!endpoints?.length) return null;
    const tName = (teacher?.Name || "").trim();
    const h = endpoints.find((e) => (e.label || "").trim() === tName);
    return h || endpoints[0];
  }, [endpoints, teacher]);

  const familyEps = React.useMemo(() => {
    if (!endpoints?.length) return [];
    if (!homeEp) return endpoints;
    return endpoints.filter((e) => e.key !== homeEp.key);
  }, [endpoints, homeEp]);

  // availability
  const DAY_MAP = {
    Monday: "Mon",
    Tuesday: "Tue",
    Wednesday: "Wed",
    Thursday: "Thu",
    Friday: "Fri",
    Saturday: "Sat",
    Sunday: "Sun",
  };
  const availabilityByDay = React.useMemo(() => {
    const details = teacher?.AvailabilityDetails || {};
    const map = new Map();

    const normalizeSlot = (slot) => {
      const s = toMin(String(slot?.start || ""));
      const e = toMin(String(slot?.end || ""));
      return Number.isFinite(s) && Number.isFinite(e) && e > s ? { start: s, end: e } : null;
    };

    let any = false;
    for (const [longDay, shortDay] of Object.entries(DAY_MAP)) {
      const slots = Array.isArray(details[longDay]) ? details[longDay] : [];
      if (!slots.length) continue;
      let minStart = Infinity;
      let maxEnd = -Infinity;
      for (const sl of slots) {
        const ns = normalizeSlot(sl);
        if (!ns) continue;
        minStart = Math.min(minStart, ns.start);
        maxEnd = Math.max(maxEnd, ns.end);
      }
      if (Number.isFinite(minStart) && Number.isFinite(maxEnd) && maxEnd > minStart) {
        map.set(shortDay, { start: minStart, end: maxEnd });
        any = true;
      }
    }

    if (!any) {
      ["Mon", "Tue", "Wed", "Thu", "Fri"].forEach((d) => map.set(d, { start: toMin("15:00"), end: toMin("20:00") }));
    }
    return map;
  }, [teacher]);

  // lessons
  const [unscheduled, setUnscheduled] = React.useState([]);
  React.useEffect(() => {
    const lessons = familyEps.map((e, i) => ({
      id: `L-${e.key}`,
      endpointKey: e.key,
      label: e.label || `Family ${i + 1}`,
      durationMin: 45,
    }));
    setUnscheduled(lessons);
  }, [familyEps]);

  const [placed, setPlaced] = React.useState([]); // { id, endpointKey, label, durationMin, day, startMin, endMin, held }

  // grid
  const [gridSpan, setGridSpan] = React.useState({ start: toMin("15:00"), end: toMin("20:00") });
  React.useEffect(() => {
    let s = Infinity,
      e = -Infinity;
    for (const d of DAY_CODES) {
      const a = availabilityByDay.get(d);
      if (!a) continue;
      s = Math.min(s, a.start);
      e = Math.max(e, a.end);
    }
    if (!Number.isFinite(s) || !Number.isFinite(e)) {
      s = toMin("15:00");
      e = toMin("20:00");
    }
    setGridSpan({ start: s, end: e });
  }, [availabilityByDay]);

  const slots = React.useMemo(() => {
    const arr = [];
    for (let m = gridSpan.start; m <= gridSpan.end; m += 15) arr.push(m);
    return arr;
  }, [gridSpan]);
  const pct = React.useCallback((m) => ((m - gridSpan.start) / (gridSpan.end - gridSpan.start)) * 100, [gridSpan]);

  // hover guide
  const [hoverGuide, setHoverGuide] = React.useState(null);

  // DnD
  const [dragId, setDragId] = React.useState(null);
  const [overUnsched, setOverUnsched] = React.useState(false);

  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragEnd = () => {
    setDragId(null);
    setHoverGuide(null);
    setOverUnsched(false);
  };

  const onDragOver = (e) => e.preventDefault();

  const canPlace = (day, startMin, durationMin) => {
    const a = availabilityByDay.get(day);
    if (!a) return false;
    const endMin = startMin + durationMin;
    for (const p of placed.filter((p) => p.day === day)) {
      if (!(endMin <= p.startMin || startMin >= p.endMin)) return false;
    }
    return startMin >= a.start && endMin <= a.end;
  };
  const placeLesson = (lesson, day, startMin, held = false) => {
    const endMin = startMin + lesson.durationMin;
    setPlaced((prev) => [...prev, { ...lesson, day, startMin, endMin, held }]);
    setUnscheduled((prev) => prev.filter((u) => u.id !== lesson.id));
  };

  const onDrop = (e, weekday, slotMin) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || dragId;
    if (!id) return;
    const fromUnscheduled = unscheduled.find((u) => u.id === id);
    const fromPlaced = placed.find((p) => p.id === id);
    const lesson = fromUnscheduled || fromPlaced;
    if (!lesson) return;

    if (!canPlace(weekday, slotMin, lesson.durationMin)) return;

    if (fromPlaced) {
      setPlaced((prev) =>
        prev.map((p) => (p.id === id ? { ...p, day: weekday, startMin: slotMin, endMin: slotMin + p.durationMin } : p))
      );
    } else {
      placeLesson(lesson, weekday, slotMin, false);
    }
    setDragId(null);
    setHoverGuide(null);
  };

  const resetBoard = () => {
    setPlaced([]);
    const lessons = familyEps.map((e, i) => ({
      id: `L-${e.key}`,
      endpointKey: e.key,
      label: e.label || `Family ${i + 1}`,
      durationMin: 45,
    }));
    setUnscheduled(lessons);
  };

  const onDragOverSlot = (e, day, slotMin) => {
    e.preventDefault();
    if (!draggingLesson) return;
    const ok = canPlace(day, slotMin, draggingLesson.durationMin);
    setHoverGuide({ day, startMin: slotMin, valid: ok });
  };

  // dragging lesson
  const draggingLesson = React.useMemo(() => {
    if (!dragId) return null;
    return unscheduled.find((u) => u.id === dragId) || placed.find((p) => p.id === dragId) || null;
  }, [dragId, unscheduled, placed]);

  // Unscheduled drop target handlers
  const onDragOverUnscheduled = (e) => {
    if (!dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setOverUnsched(true);
  };
  const onDropToUnscheduled = (e) => {
    e.preventDefault();
    setOverUnsched(false);
    const id = e.dataTransfer.getData("text/plain") || dragId;
    if (!id) return;
    const fromPlaced = placed.find((p) => p.id === id);
    const alreadyUnscheduled = unscheduled.some((u) => u.id === id);
    if (fromPlaced && !alreadyUnscheduled) {
      setPlaced((prev) => prev.filter((x) => x.id !== id));
      setUnscheduled((prev) => [
        { id: fromPlaced.id, endpointKey: fromPlaced.endpointKey, label: fromPlaced.label, durationMin: fromPlaced.durationMin },
        ...prev,
      ]);
    }
  };
  const onDragLeaveUnscheduled = () => setOverUnsched(false);

   const toggleHold = (id) => setPlaced(prev => prev.map(x => x.id === id ? { ...x, held: !x.held } : x));

  // label lookup (unused but handy)
  const labelOf = (key) => endpoints.find((e) => e.key === key)?.label || key;

  // UI
  return (
    <div className="rounded-2xl border border-slate-200 p-4 bg-white mt-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-slate-900">Interactive Optimizer (drag & drop)</h3>
        <div className="text-xs text-slate-500">{`${endpoints.length} endpoints`}</div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={resetBoard} className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-800 text-sm">
          Reset board
        </button>
        {homeEp && (
          <span className="text-xs text-slate-600">
            Start/End at <span className="font-medium">{homeEp.label}'s Home</span>
          </span>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Unscheduled (now a drop target) */}
        <div className="col-span-3">
          <div className="rounded-xl border border-slate-200">
            <div className="px-3 py-2 text-sm font-medium text-slate-800 bg-slate-50 rounded-t-xl">Unscheduled</div>
            <div
              className={`p-2 space-y-2 min-h-[200px] rounded-b-xl transition
                ${overUnsched ? "ring-2 ring-blue-400 bg-blue-50" : ""}`}
              onDragOver={onDragOverUnscheduled}
              onDrop={onDropToUnscheduled}
              onDragLeave={onDragLeaveUnscheduled}
            >
              {unscheduled.length === 0 ? (
                <div className="text-sm text-slate-500 px-2 py-4">Drop here to remove from schedule</div>
              ) : (
                unscheduled.map((u) => (
                  <div
                    key={u.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, u.id)}
                    onDragEnd={onDragEnd}
                    className="rounded-lg border border-slate-200 p-2 bg-blue-100 cursor-grab active:cursor-grabbing"
                    title="Drag to a day/time"
                  >
                    <div className="text-sm font-medium text-slate-900">{u.label}</div>
                    <div className="text-xs text-slate-600">{u.durationMin} min</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Week grid */}
        <div className="col-span-9">
          <div className="grid grid-cols-8 gap-2">
            <div className="text-xs text-slate-600" />
            {DAY_CODES.map((d) => (
              <div key={d} className="text-xs font-medium text-slate-700 text-center">
                {d}
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            {/* time gutter */}
            <div className="w-16 relative">
              <div className="relative h-[640px] border-l border-slate-200">
                {Array.from({ length: (gridSpan.end - gridSpan.start) / 60 + 1 }).map((_, idx) => {
                  const m = gridSpan.start + idx * 60;
                  return (
                    <div
                      key={m}
                      className="absolute left-0 w-full text-[10px] text-slate-500 -translate-y-1/2"
                      style={{ top: `${pct(m)}%` }}
                    >
                      {toHHMM(m)}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 7 day columns */}
            <div className="grid grid-cols-7 gap-2 flex-1">
              {DAY_CODES.map((day) => {
                const a = availabilityByDay.get(day);
                const dayPlaced = placed.filter((p) => p.day === day).sort((x, y) => x.startMin - y.startMin);

                // drive chips (Home→first, between, last→Home)
                const segments = [];
                if (a && dayPlaced.length) {
                  const first = dayPlaced[0];
                  const last = dayPlaced[dayPlaced.length - 1];
                  if (homeEp) {
                    const mStart = lookupMinutes(minutesMap, homeEp.key, first.endpointKey);
                    if (Number.isFinite(mStart)) {
                      segments.push({
                        minutes: mStart,
                        topPct: (pct(a.start) + pct(first.startMin)) / 2,
                        from: homeEp.label,
                        to: first.label,
                      });
                    }
                  }
                  for (let i = 0; i < dayPlaced.length - 1; i++) {
                    const A = dayPlaced[i],
                      B = dayPlaced[i + 1];
                    const mAB = lookupMinutes(minutesMap, A.endpointKey, B.endpointKey);
                    if (Number.isFinite(mAB)) {
                      segments.push({
                        minutes: mAB,
                        topPct: (pct(A.endMin) + pct(B.startMin)) / 2,
                        from: A.label,
                        to: B.label,
                      });
                    }
                  }
                  if (homeEp) {
                    const mEnd = lookupMinutes(minutesMap, last.endpointKey, homeEp.key);
                    if (Number.isFinite(mEnd)) {
                      segments.push({
                        minutes: mEnd,
                        topPct: (pct(last.endMin) + pct(a.end)) / 2,
                        from: last.label,
                        to: homeEp.label,
                      });
                    }
                  }
                }
                const dayDriveTotal = segments.reduce((s, seg) => s + (Number.isFinite(seg.minutes) ? seg.minutes : 0), 0);

                return (
                  <div key={day} className="relative h-[640px] bg-slate-300 rounded-xl border border-slate-200 overflow-hidden">
                    {/* availability shading */}
                    {a && (
                      <div
                        className="absolute left-0 right-0 bg-white/80"
                        style={{ top: `${pct(a.start)}%`, height: `${pct(a.end) - pct(a.start)}%` }}
                      />
                    )}
                    {/* hour lines */}
                    {Array.from({ length: (gridSpan.end - gridSpan.start) / 60 + 1 }).map((_, idx) => {
                      const m = gridSpan.start + idx * 60;
                      return <div key={m} className="absolute left-0 right-0 h-px bg-blue-200" style={{ top: `${pct(m)}%` }} />;
                    })}

                    {/* drop zones (use slot handler) */}
                    {a &&
                      slots
                        .filter((m) => m >= a.start && m + 15 <= a.end)
                        .map((m) => (
                          <div
                            key={m}
                            onDragOver={(e) => onDragOverSlot(e, day, m)}
                            onDrop={(e) => {
                              onDrop(e, day, m);
                              setHoverGuide(null);
                            }}
                            className="absolute left-0 right-0"
                            style={{ top: `${pct(m)}%`, height: `${pct(m + 15) - pct(m)}%` }}
                          />
                        ))}

                    {/* placed */}
                    {dayPlaced.map((p) => (
                      <div
                        key={p.id}
                        draggable
                        onDragStart={(e) => onDragStart(e, p.id)}
                        onDragEnd={onDragEnd}
                        className={`absolute left-1 right-1 rounded-lg border px-2 py-1.5 shadow-sm ${
                          p.held ? "bg-blue-600 text-white border-blue-700" : "bg-blue-200 text-slate-900 border-slate-300"
                        } cursor-grab`}
                        style={{ top: `${pct(p.startMin)}%`, height: `${pct(p.endMin) - pct(p.startMin)}%` }}
                        title={`${p.label} • ${toHHMM(p.startMin)}–${toHHMM(p.endMin)} • ${p.held ? "Held " : ""}`}
                      >
                        <div className="text-[11px] font-semibold">
                          {toHHMM(p.startMin)}–{toHHMM(p.endMin)}
                        </div>
                        <div className="text-xs truncate" onClick={() => toggleHold(p.id)}>
                          {p.label}
                        </div>
                      </div>
                    ))}

                    {/* live drive chips (never block drops) */}
                    {segments.map((seg, i) => (
                      <div
                        key={`chip-${i}`}
                        className="pointer-events-none absolute left-1/2 -translate-x-1/2 -translate-y-1/2 border-green-500 border border-solid text-green-700 text-[10px] px-2 py-0.5 rounded-full flex text-center shadow z-20"
                        style={{ top: `${seg.topPct}%` }}
                        title={`Drive ${seg.minutes}m (${seg.from} → ${seg.to})`}
                      >
                        Drive {seg.minutes}m
                      </div>
                    ))}

                    {/* Hover guide line + time chip */}
                    {hoverGuide && hoverGuide.day === day && (
                      <>
                        <div
                          className={`absolute left-0 right-0 h-[2px] pointer-events-none ${
                            hoverGuide.valid ? "bg-green-500" : "bg-red-500"
                          }`}
                          style={{ top: `${pct(hoverGuide.startMin)}%` }}
                        />
                        <div
                          className={`absolute -translate-y-1/2 pointer-events-none px-1.5 py-0.5 text-[10px] rounded-md shadow border ${
                            hoverGuide.valid
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-red-50 text-red-700 border-red-200"
                          }`}
                          style={{ top: `${pct(hoverGuide.startMin)}%`, left: "0.25rem" }}
                        >
                          {toHHMM(hoverGuide.startMin)}
                        </div>
                      </>
                    )}

                    {/* day total */}
                    {a && (
                      <div
                        className="absolute top-1 right-1 bg-slate-900 text-white text-[10px] px-2 py-0.5 rounded-full shadow"
                        title="Total drive time (including Home legs)"
                      >
                        {dayDriveTotal}m
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 text-xs text-slate-500">
        Drag lessons to days/times to place; drag them back to <b>Unscheduled</b> to remove.
      </div>
    </div>
  );
}

/* ===================== Main page ===================== */

const Scheduling2025 = () => {
  const [school, setSchool] = React.useState("");
  const [teachers, setTeachers] = React.useState([]);
  const [teacher, setTeacher] = React.useState(null);
  const [bucketHHMM, setBucketHHMM] = React.useState("16:00");

  React.useEffect(() => {
    if (!school) {
      setTeachers([]);
      setTeacher(null);
      return;
    }
    axios
      .get(`${baseUrl}/teachersbyschool/${school}`, getRequestHeaders())
      .then((response) => {
        response.data.sort((a, b) => a.Name.localeCompare(b.Name));
        setTeachers(response.data);
      })
      .catch((error) => {
        console.error("Error fetching teachers:", error);
      });
  }, [school]);

  const setTeacherById = (id) => {
    const selectedTeacher = teachers.find((t) => t.TeacherID === id);
    setTeacher(selectedTeacher || null);
  };

  const { endpoints, minutesMap, loading, err } = useTeacherNetwork({
    teacherId: teacher?.TeacherID,
    schoolId: school,
    bucketHHMM,
  });

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-xl font-semibold">Scheduling 2025</h1>

      <div className="flex flex-wrap gap-3 items-end">
        {/* school selector */}
        <label className="text-sm">
          <div className="text-slate-600 mb-1">School</div>
          <select
            value={school}
            onChange={(e) => setSchool(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-1.5"
          >
            <option value="">Select a school</option>
            <option value="mma">Mountainside</option>
            <option value="lsma">Lakeside</option>
            <option value="tma">Treeside</option>
            <option value="mor">Morningside</option>
            <option value="psma">Parkside</option>
          </select>
        </label>

        {/* teacher selector */}
        <label className="text-sm">
          <div className="text-slate-600 mb-1">Teacher</div>
          <select
            value={teacher ? teacher.TeacherID : ""}
            onChange={(e) => setTeacherById(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 min-w-[220px]"
            disabled={!teachers.length}
          >
            <option value="">Select a teacher</option>
            {teachers.map((t) => (
              <option key={t.TeacherID} value={t.TeacherID}>
                {t.Name}
              </option>
            ))}
          </select>
        </label>

        {/* bucket */}
        <label className="text-sm">
          <div className="text-slate-600 mb-1">Bucket (HH:MM)</div>
          <input
            value={bucketHHMM}
            onChange={(e) => setBucketHHMM(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 w-28"
            placeholder="16:00"
          />
        </label>

        <div className="text-xs text-slate-500 h-9 flex items-center">
          {loading ? "Loading drive matrix…" : err ? <span className="text-red-600">{err}</span> : teacher ? `${endpoints.length} endpoints loaded` : "Pick a teacher to load endpoints"}
        </div>
      </div>

      {teacher && endpoints.length > 0 ? (
        <InteractiveOptimizer teacher={teacher} endpoints={endpoints} minutesMap={minutesMap} />
      ) : null}
    </div>
  );
};

export default Scheduling2025;















// import React, { useEffect, useMemo, useState } from "react";
// import {
//   DEMO_SEMESTER_DATA,
//   DEMO_TEACHERS_DATA,
//   DEMO_HOLDS,
//   getTeacherDay,
// } from "./demoData";
// import axios from "axios";
// import { baseUrl } from "../assets/Animations";




// /** Weekly Calendar (7 columns) — demo mode, plus Drive Matrix panel */

// const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// // add local IDs (demo data has no _id)
// const toId = (s) =>
//   s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// function useSemesters() {
//   const [semesters, setSemesters] = useState([]);
//   useEffect(() => {
//     setSemesters(DEMO_SEMESTER_DATA.map((s) => ({ id: toId(s.label), label: s.label, ...s })));
//   }, []);
//   return { semesters, loading: false };
// }
// function useTeachers() {
//   const [teachers, setTeachers] = useState([]);
//   useEffect(() => {
//     setTeachers(DEMO_TEACHERS_DATA.map((t) => ({ id: toId(t.name), name: t.name, ...t })));
//   }, []);
//   return { teachers, loading: false };
// }

// function toMin(hhmm) {
//   const [H, M] = hhmm.split(":").map(Number);
//   return H * 60 + M;
// }
// function toHHMM(min) {
//   const H = Math.floor(min / 60);
//   const M = min % 60;
//   return `${String(H).padStart(2, "0")}:${String(M).padStart(2, "0")}`;
// }

// function StatCard({ label, value, sub }) {
//   return (
//     <div className="rounded-2xl border border-slate-200 p-4 shadow-sm bg-white">
//       <div className="text-sm text-slate-500">{label}</div>
//       <div className="text-2xl font-semibold text-slate-900 mt-1">{value}</div>
//       {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
//     </div>
//   );
// }
// function Tag({ children, tone = "slate" }) {
//   const map = {
//     slate: "bg-slate-900 text-white",
//     green: "bg-green-100 text-green-700",
//     red: "bg-red-100 text-red-700",
//     blue: "bg-blue-100 text-blue-700",
//     amber: "bg-amber-100 text-amber-800",
//     fuchsia: "bg-fuchsia-100 text-fuchsia-700",
//   };
//   return (
//     <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full ${map[tone] || map.slate}`}>
//       {children}
//     </span>
//   );
// }

// function Toolbar({ semesters, teachers, value, onChange }) {
//   const { semesterId, teacherId } = value;
//   return (
//     <div className="flex flex-col md:flex-row gap-3 md:items-end">
//       <div className="flex-1">
//         <label className="block text-sm text-slate-600 mb-1">Semester</label>
//         <select
//           className="w-full rounded-xl border-slate-300 focus:ring-2 focus:ring-blue-500"
//           value={semesterId || ""}
//           onChange={(e) => onChange({ ...value, semesterId: e.target.value })}
//         >
//           <option value="">Select…</option>
//           {semesters.map((s) => (
//             <option key={s.id} value={s.id}>
//               {s.label}
//             </option>
//           ))}
//         </select>
//       </div>

//       <div className="flex-1">
//         <label className="block text-sm text-slate-600 mb-1">Teacher</label>
//         <select
//           className="w-full rounded-xl border-slate-300 focus:ring-2 focus:ring-blue-500"
//           value={teacherId || ""}
//           onChange={(e) => onChange({ ...value, teacherId: e.target.value })}
//         >
//           <option value="">Select…</option>
//           {teachers.map((t) => (
//             <option key={t.id} value={t.id}>
//               {t.name}
//             </option>
//           ))}
//         </select>
//       </div>
//     </div>
//   );
// }

// /** Column calendar: 7 day-columns, shared vertical time scale */
// function WeekCalendar({ week }) {
//   // global vertical scale across week
//   const [earliest, latest] = useMemo(() => {
//     const starts = week.flatMap((d) => d.timeline.map((b) => toMin(b.start)));
//     const ends = week.flatMap((d) => d.timeline.map((b) => toMin(b.end)));
//     const e = starts.length ? Math.min(...starts) : toMin("15:00");
//     const l = ends.length ? Math.max(...ends) : toMin("20:00");
//     return [e, l];
//   }, [week]);

//   const span = Math.max(60, latest - earliest);
//   const y = (hhmm) => ((toMin(hhmm) - earliest) / span) * 100; // top %
//   const hPct = (durMin) => (durMin / span) * 100;

//   const HOURS = useMemo(() => {
//     const startHour = Math.floor(earliest / 60);
//     const endHour = Math.ceil(latest / 60);
//     return Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i);
//   }, [earliest, latest]);

//   return (
//     <div className="w-full">
//       {/* Header row */}
//       <div className="grid grid-cols-8 gap-2 mb-2">
//         <div />{/* time gutter */}
//         {WEEKDAYS.map((d) => (
//           <div key={d} className="text-sm font-medium text-slate-700 text-center">{d}</div>
//         ))}
//       </div>

//       <div className="flex gap-2">
//         {/* Time gutter (hour labels) */}
//         <div className="w-16 relative">
//           <div className="relative h-[640px]">
//             {HOURS.map((hr) => (
//               <div
//                 key={hr}
//                 className="absolute left-0 w-full text-[11px] text-slate-500 -translate-y-1/2"
//                 style={{ top: `${y(`${String(hr).padStart(2, "0")}:00`)}%` }}
//               >
//                 {String(hr).padStart(2, "0")}:00
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* 7 columns */}
//         <div className="grid grid-cols-7 gap-2 flex-1">
//           {WEEKDAYS.map((weekday) => {
//             const day = week.find((d) => d.weekday === weekday) || {
//               weekday,
//               timeline: [],
//             };
//             return (
//               <div key={weekday} className="relative h-[640px] bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
//                 {/* hour grid lines */}
//                 {HOURS.map((hr) => (
//                   <div
//                     key={hr}
//                     className="absolute left-0 right-0 h-px bg-slate-200"
//                     style={{ top: `${y(`${String(hr).padStart(2, "0")}:00`)}%` }}
//                   />
//                 ))}

//                 {/* booking blocks */}
//                 {day.timeline.map((b, idx) => (
//                   <div
//                     key={idx}
//                     className="absolute left-1 right-1 rounded-lg shadow-sm border border-slate-300 bg-white px-2 py-1.5"
//                     style={{
//                       top: `${y(b.start)}%`,
//                       height: `${hPct(b.durationMin)}%`,
//                     }}
//                     title={`${b.familyName} (${b.instrument}) ${b.start}–${b.end}`}
//                   >
//                     <div className="text-[11px] font-semibold text-slate-900">
//                       {b.start}–{b.end}
//                     </div>
//                     <div className="text-xs text-slate-700 truncate">{b.familyName}</div>
//                     <div className="mt-1">
//                       <Tag tone="blue">{b.instrument}</Tag>
//                     </div>
//                   </div>
//                 ))}

//                 {/* gaps + drive chips (between blocks) */}
//                 {day.timeline.map((b, idx) => {
//                   const next = day.timeline[idx + 1];
//                   if (!next) return null;

//                   const gapMin = Math.max(0, toMin(next.start) - toMin(b.end));
//                   const hasGap = gapMin > 0;

//                   const topGap = y(b.end);
//                   const heightGap = hPct(gapMin);
//                   const chipTop = topGap + heightGap / 2;

//                   return (
//                     <div key={`gap-${idx}`}>
//                       {hasGap && (
//                         <div
//                           className="absolute left-2 right-2 bg-amber-50/70 border border-dashed border-amber-300 rounded-md flex items-center justify-center text-[10px] text-amber-700"
//                           style={{ top: `${topGap}%`, height: `${heightGap}%` }}
//                           title={`Gap ${gapMin}m`}
//                         >
//                           Gap {gapMin}m
//                         </div>
//                       )}
//                       <div
//                         className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900 text-white text-[10px] px-2 py-0.5 rounded-full shadow"
//                         style={{ top: `${chipTop}%` }}
//                         title={`Drive ${next.driveFromPrevMin ?? 0}m`}
//                       >
//                         Drive {next.driveFromPrevMin ?? 0}m
//                       </div>
//                     </div>
//                   );
//                 })}
//               </div>
//             );
//           })}
//         </div>
//       </div>
//     </div>
//   );
// }

// /* ---------------------- DRIVE MATRIX ---------------------- */

// /**
//  * Fetch endpoints first, then minutes for every off-diagonal pair.
//  * Expects:
//  *   GET /manager/drive-matrix/:teacherDbId -> { endpoints:[{lat,lng,key,placeId?}...], size, todBucket }
//  *   GET /manager/drive-minutes?teacherId=...&fromKey=...&toKey=...&bucket=HH:MM -> { minutes: number|null }
//  */
// function useDriveMatrix(teacherDbId, bucketHHMM) {
//   const [endpoints, setEndpoints] = useState([]);
//   const [todBucket, setTodBucket] = useState("");
//   const [minutesMap, setMinutesMap] = useState(new Map()); // "ok|dk" -> minutes
//   const [loading, setLoading] = useState(false);
//   const [progress, setProgress] = useState({ done: 0, total: 0 });
//   const [err, setErr] = useState("");

//   useEffect(() => {
//     if (!teacherDbId) return;
//     let cancelled = false;
//     const fetchAll = async () => {
//       setLoading(true);
//       setErr("");
//       setMinutesMap(new Map());
//       setProgress({ done: 0, total: 0 });
//       try {
//         // 1) endpoints
//         const meta = await axios.get(`${baseUrl}/manager/drive-matrix/${teacherDbId}`);
//         console.log(meta)
//         const eps = meta.data?.endpoints || [];
//         if (cancelled) return;
//         setEndpoints(eps);
//         setTodBucket(meta.data?.todBucket || "");

//         // 2) build pair list
//         const keys = eps.map((e) => e.key || e.placeId);
//         const tasks = [];
//         for (let i = 0; i < keys.length; i++) {
//           for (let j = 0; j < keys.length; j++) {
//             if (i === j) continue;
//             const ok = keys[i];
//             const dk = keys[j];
//             tasks.push({ ok, dk });
//           }
//         }
//         setProgress({ done: 0, total: tasks.length });

//         // 3) fetch with limited concurrency
//         const limit = 8;
//         let done = 0;
//         const localMap = new Map();

//         async function worker(queue) {
//           while (queue.length) {
//             const { ok, dk } = queue.shift();
//             try {
//               const res = await axios.get(`${baseUrl}/manager/drive-minutes`, {
//                 params: { teacherId: teacherDbId, fromKey: ok, toKey: dk, bucket: bucketHHMM },
//               });
//               const m = res.data?.minutes ?? null;
//               localMap.set(`${ok}|${dk}`, Number.isFinite(m) ? m : null);
//             } catch (e) {
//               localMap.set(`${ok}|${dk}`, null);
//             } finally {
//               done++;
//               if (!cancelled) setProgress({ done, total: tasks.length });
//             }
//           }
//         }

//         const queue = tasks.slice();
//         await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, () => worker(queue)));

//         if (!cancelled) setMinutesMap(localMap);
//       } catch (e) {
//         if (!cancelled) setErr(e?.response?.data?.error || e.message || "Failed to load drive matrix");
//       } finally {
//         if (!cancelled) setLoading(false);
//       }
//     };
//     fetchAll();
//     return () => {
//       cancelled = true;
//     };
//   }, [teacherDbId, bucketHHMM]);

//   return { endpoints, todBucket, minutesMap, loading, progress, err };
// }

// function DriveMatrixPanel({ teacherDbId, bucketHHMM }) {
//   const { endpoints, todBucket, minutesMap, loading, progress, err } = useDriveMatrix(teacherDbId, bucketHHMM);

//   const {
//     epsSorted,
//     keys,
//     matrix,
//     minMin,
//     maxMin,
//     pairsSortedAsc,
//     pairsSortedDesc,
//   } = useMemo(() => {
//     const labelOf = (ep) =>
//       ep.label ||
//       (ep.placeId ? ep.placeId.slice(-6) : `${ep.lat?.toFixed?.(3)},${ep.lng?.toFixed?.(3)}`);

//     const epsSorted = [...endpoints]
//       .map((e) => ({ ...e, label: labelOf(e), key: e.key || e.placeId }))
//       .sort((a, b) => (a.label || "").localeCompare(b.label || ""));

//     const keys = epsSorted.map((e) => e.key);

//     // Build matrix
//     let localMin = Infinity;
//     let localMax = -Infinity;

//     const matrix = keys.map((ok, ri) =>
//       keys.map((dk, ci) => {
//         if (ok === dk) return null;
//         const v = minutesMap.get(`${ok}|${dk}`);
//         if (Number.isFinite(v)) {
//           if (v < localMin) localMin = v;
//           if (v > localMax) localMax = v;
//           return v;
//         }
//         return null;
//       })
//     );

//     // build sorted lists
//     const pairs = [];
//     for (const ok of keys) {
//       for (const dk of keys) {
//         if (ok === dk) continue;
//         const v = minutesMap.get(`${ok}|${dk}`);
//         if (Number.isFinite(v)) {
//           const from = epsSorted.find((e) => e.key === ok);
//           const to = epsSorted.find((e) => e.key === dk);
//           pairs.push({ from, to, minutes: v });
//         }
//       }
//     }
//     const pairsSortedAsc = [...pairs].sort((a, b) => a.minutes - b.minutes);
//     const pairsSortedDesc = [...pairs].sort((a, b) => b.minutes - a.minutes);

//     return {
//       epsSorted,
//       keys,
//       matrix,
//       minMin: Number.isFinite(localMin) ? localMin : 0,
//       maxMin: Number.isFinite(localMax) ? localMax : 0,
//       pairsSortedAsc,
//       pairsSortedDesc,
//     };
//   }, [endpoints, minutesMap]);

//   const colorFor = (m) => {
//     if (m == null) return "transparent";
//     if (maxMin <= minMin) return "hsl(120 60% 45%)";
//     const t = (m - minMin) / (maxMin - minMin); // 0..1
//     const hue = 120 - 120 * t; // green -> red
//     return `hsl(${hue} 70% 50%)`;
//   };

//   return (
//     <div className="rounded-2xl border border-slate-200 p-4 bg-white">
//       <div className="flex items-center justify-between mb-3">
//         <h3 className="text-base font-semibold text-slate-900">Drive Matrix (heatmap)</h3>
//         <div className="text-xs text-slate-500">
//           {loading ? (
//             <span>Loading pairs… {progress.done}/{progress.total}</span>
//           ) : err ? (
//             <span className="text-red-600">{err}</span>
//           ) : (
//             <span>
//               {minutesMap.size} pairs · {todBucket || `bucket ${bucketHHMM}`}
//             </span>
//           )}
//         </div>
//       </div>

//       {!loading && !err && endpoints.length === 0 && (
//         <div className="text-sm text-slate-500">No drive endpoints available.</div>
//       )}

//       {/* Legend */}
//       {!loading && endpoints.length > 0 && (
//         <div className="flex items-center gap-3 mb-4">
//           <div className="text-xs text-slate-600">Legend:</div>
//           <div className="h-3 w-40 rounded-full bg-gradient-to-r from-green-500 to-red-500" />
//           <div className="text-xs text-slate-500">
//             {minMin}m → {maxMin}m
//           </div>
//         </div>
//       )}

//       {/* Heatmap */}
//       {!loading && endpoints.length > 0 && (
//         <div className="overflow-auto rounded-xl border border-slate-200">
//           <table className="min-w-[640px] table-fixed">
//             <thead className="bg-slate-50 sticky top-0 z-10">
//               <tr>
//                 <th className="w-40 p-2 text-left text-xs font-medium text-slate-600 sticky left-0 bg-slate-50 z-20">
//                   From \ To
//                 </th>
//                 {epsSorted.map((ep) => (
//                   <th
//                     key={ep.key}
//                     className="p-2 text-left text-[11px] font-medium text-slate-600"
//                     title={`${ep.label}\n${ep.lat?.toFixed?.(4)}, ${ep.lng?.toFixed?.(4)}`}
//                   >
//                     {ep.label}
//                   </th>
//                 ))}
//               </tr>
//             </thead>
//             <tbody>
//               {epsSorted.map((from, ri) => (
//                 <tr key={from.key} className="odd:bg-white even:bg-slate-50/40">
//                   <th
//                     className="w-40 p-2 text-left text-[11px] text-slate-700 font-medium sticky left-0 bg-inherit z-10"
//                     title={`${from.label}\n${from.lat?.toFixed?.(4)}, ${from.lng?.toFixed?.(4)}`}
//                   >
//                     {from.label}
//                   </th>
//                   {epsSorted.map((to, ci) => {
//                     const m = matrix[ri]?.[ci] ?? null;
//                     const isDiag = ri === ci;
//                     return (
//                       <td
//                         key={to.key}
//                         className={`p-1 text-center align-middle ${isDiag ? "bg-slate-100 text-slate-400" : "text-slate-900"}`}
//                         title={isDiag ? "Same location" : `From ${from.label} → ${to.label} — ${m ?? "—"}m`}
//                       >
//                         {isDiag ? (
//                           <span className="text-[11px]">—</span>
//                         ) : m == null ? (
//                           <span className="text-[11px] text-slate-400">—</span>
//                         ) : (
//                           <div
//                             className="mx-auto rounded-sm text-[11px] font-medium text-white"
//                             style={{
//                               width: "100%",
//                               minWidth: 36,
//                               background: colorFor(m),
//                               padding: "6px 4px",
//                             }}
//                           >
//                             {m}m
//                           </div>
//                         )}
//                       </td>
//                     );
//                   })}
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       )}

//       {/* Quick lists */}
//       {!loading && endpoints.length > 0 && (
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
//           <div className="rounded-xl border border-slate-200 p-3">
//             <div className="text-sm font-medium text-slate-800 mb-2">Shortest drives</div>
//             <ul className="space-y-1">
//               {pairsSortedAsc.slice(0, 10).map((p, i) => (
//                 <li key={`s-${i}`} className="text-sm text-slate-700">
//                   <span className="font-medium">{p.from.label}</span> → <span className="font-medium">{p.to.label}</span>{" "}
//                   <span className="text-slate-500">({p.minutes}m)</span>
//                 </li>
//               ))}
//             </ul>
//           </div>
//           <div className="rounded-xl border border-slate-200 p-3">
//             <div className="text-sm font-medium text-slate-800 mb-2">Longest drives</div>
//             <ul className="space-y-1">
//               {pairsSortedDesc.slice(0, 10).map((p, i) => (
//                 <li key={`l-${i}`} className="text-sm text-slate-700">
//                   <span className="font-medium">{p.from.label}</span> → <span className="font-medium">{p.to.label}</span>{" "}
//                   <span className="text-slate-500">({p.minutes}m)</span>
//                 </li>
//               ))}
//             </ul>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }



// /* ---------------------- INTERACTIVE OPTIMIZER (DnD + live drive chips) ---------------------- */

// function minutesBetween(minutesMap, fromKey, toKey) {
//   if (!fromKey || !toKey) return null;
//   const v = minutesMap.get(`${fromKey}|${toKey}`);
//   return Number.isFinite(v) ? v : null;
// }
// const DAY_CODES = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];


// function InteractiveOptimizer({ teacher, teacherDbId, bucketHHMM }) {
//   // 1) drive matrix
//   const { endpoints, minutesMap, loading, progress, err } = useDriveMatrix(teacherDbId, bucketHHMM);

//   // 2) home + families
//   const homeEp = React.useMemo(() => {
//     if (!endpoints?.length) return null;
//     const h = endpoints.find(e => (e.label||"").toLowerCase().includes("home"));
//     return h || endpoints[0];
//   }, [endpoints]);
//   const familyEps = React.useMemo(
//     () => (endpoints || []).filter(e => (homeEp ? e.key !== homeEp.key : true)),
//     [endpoints, homeEp]
//   );

//   // 3) availability
//   const availabilityByDay = React.useMemo(() => {
//     const avail = teacher?.weeklyAvailability?.length ? teacher.weeklyAvailability : [
//       { day:"Mon", start:"15:00", end:"20:00" },
//       { day:"Tue", start:"15:00", end:"20:00" },
//       { day:"Wed", start:"15:00", end:"20:00" },
//       { day:"Thu", start:"15:00", end:"20:00" },
//       { day:"Fri", start:"15:00", end:"20:00" },
//     ];
//     const map = new Map();
//     for (const a of avail) map.set(a.day, { start: toMin(a.start), end: toMin(a.end) });
//     return map;
//   }, [teacher]);

//   // 4) lessons
//   const [unscheduled, setUnscheduled] = React.useState([]);
//   React.useEffect(() => {
//     const lessons = familyEps.map((e, i) => ({
//       id: `L-${e.key}`,
//       endpointKey: e.key,
//       label: e.label || `Family ${i+1}`,
//       durationMin: 45,
//     }));
//     setUnscheduled(lessons);
//   }, [familyEps]);

//   const [placed, setPlaced] = React.useState([]); // { id, endpointKey, label, durationMin, day, startMin, endMin, held }

//   // 5) grid span (shared vertically)
//   const [gridSpan, setGridSpan] = React.useState({ start: toMin("15:00"), end: toMin("20:00") });
//   React.useEffect(() => {
//     let s = Infinity, e = -Infinity;
//     for (const d of DAY_CODES) {
//       const a = availabilityByDay.get(d);
//       if (!a) continue;
//       s = Math.min(s, a.start);
//       e = Math.max(e, a.end);
//     }
//     if (!Number.isFinite(s) || !Number.isFinite(e)) { s = toMin("15:00"); e = toMin("20:00"); }
//     setGridSpan({ start: s, end: e });
//   }, [availabilityByDay]);
//   const slots = React.useMemo(() => {
//     const arr = [];
//     for (let m = gridSpan.start; m <= gridSpan.end; m += 15) arr.push(m);
//     return arr;
//   }, [gridSpan]);
//   const pct = React.useCallback(
//     (m) => ((m - gridSpan.start) / (gridSpan.end - gridSpan.start)) * 100,
//     [gridSpan]
//   );

//   // DnD
//   const [dragId, setDragId] = React.useState(null);
//   const onDragStart = (e, id) => { setDragId(id); e.dataTransfer.setData("text/plain", id); };
//   const onDragOver = (e) => e.preventDefault();

//   // placement helpers
//   const canPlace = (day, startMin, durationMin) => {
//     const a = availabilityByDay.get(day);
//     if (!a) return false;
//     const endMin = startMin + durationMin;
//     // overlap check same day
//     for (const p of placed.filter(p => p.day === day)) {
//       if (!(endMin <= p.startMin || startMin >= p.endMin)) return false;
//     }
//     return startMin >= a.start && endMin <= a.end;
//   };
//   const placeLesson = (lesson, day, startMin, held=false) => {
//     const endMin = startMin + lesson.durationMin;
//     setPlaced(prev => [...prev, { ...lesson, day, startMin, endMin, held }]);
//     setUnscheduled(prev => prev.filter(u => u.id !== lesson.id));
//   };
//   const removePlaced = (id) => {
//     const p = placed.find(x => x.id === id);
//     if (!p) return;
//     setPlaced(prev => prev.filter(x => x.id !== id));
//     setUnscheduled(prev => [{ id:p.id, endpointKey:p.endpointKey, label:p.label, durationMin:p.durationMin }, ...prev]);
//   };
//   const toggleHold = (id) => setPlaced(prev => prev.map(x => x.id === id ? { ...x, held: !x.held } : x));

//   const onDrop = (e, weekday, slotMin) => {
//     e.preventDefault();
//     const id = e.dataTransfer.getData("text/plain") || dragId;
//     if (!id) return;
//     const fromUnscheduled = unscheduled.find(u => u.id === id);
//     const fromPlaced = placed.find(p => p.id === id);
//     const lesson = fromUnscheduled || fromPlaced;
//     if (!lesson) return;

//     if (!canPlace(weekday, slotMin, lesson.durationMin)) return;

//     if (fromPlaced) {
//       setPlaced(prev => prev.map(p => p.id === id
//         ? { ...p, day: weekday, startMin: slotMin, endMin: slotMin + p.durationMin }
//         : p));
//     } else {
//       placeLesson(lesson, weekday, slotMin, false);
//     }
//     setDragId(null);
//   };

//   // Reoptimize (same as before, left unchanged for brevity)
//   const reoptimize = () => {
//     if (!homeEp) { alert("No Home endpoint found in matrix."); return; }
//     const keyToLabel = new Map(endpoints.map(e => [e.key, e.label || e.key]));
//     const uns = [...unscheduled];
//     const byDay = new Map(DAY_CODES.map(d => [d, placed.filter(p => p.day === d)]));
//     const newPlaced = [...placed];

//     for (const day of DAY_CODES) {
//       const a = availabilityByDay.get(day);
//       if (!a) continue;
//       const anchors = (byDay.get(day) || []).filter(p => p.held).sort((a,b)=>a.startMin-b.startMin);

//       const intervals = [];
//       if (anchors.length === 0) {
//         intervals.push({ fromTime: a.start, toTime: a.end, prevKey: homeEp.key, nextKey: homeEp.key });
//       } else {
//         let prevEnd = a.start;
//         let prevKey = homeEp.key;
//         for (let i=0;i<anchors.length;i++) {
//           const H = anchors[i];
//           if (H.startMin > prevEnd) intervals.push({ fromTime: prevEnd, toTime: H.startMin, prevKey, nextKey: H.endpointKey });
//           prevEnd = H.endMin;
//           prevKey = H.endpointKey;
//           if (i === anchors.length-1 && H.endMin < a.end) {
//             intervals.push({ fromTime: H.endMin, toTime: a.end, prevKey, nextKey: homeEp.key });
//           }
//         }
//       }

//       for (const it of intervals) {
//         let t = it.fromTime;
//         let locKey = it.prevKey;
//         while (true) {
//           let best = null;
//           let bestDrive = Infinity;
//           for (const cand of uns) {
//             const d1 = minutesBetween(minutesMap, locKey, cand.endpointKey);
//             if (!Number.isFinite(d1)) continue;
//             const startAt = Math.max(t + d1, it.fromTime);
//             const endAt = startAt + cand.durationMin;
//             const d2 = minutesBetween(minutesMap, cand.endpointKey, it.nextKey);
//             if (!Number.isFinite(d2)) continue;
//             if (endAt + d2 <= it.toTime && d1 < bestDrive) best = { cand, startAt, endAt, d1 };
//             if (best) bestDrive = best.d1;
//           }
//           if (!best) break;
//           newPlaced.push({ ...best.cand, day, startMin: best.startAt, endMin: best.endAt, held: false });
//           const idx = uns.findIndex(u => u.id === best.cand.id);
//           if (idx >= 0) uns.splice(idx,1);
//           t = best.endAt;
//           locKey = best.cand.endpointKey;
//         }
//       }
//     }

//     const heldOnly = placed.filter(p => p.held);
//     const keptHeld = new Set(heldOnly.map(p => p.id));
//     const merged = [...heldOnly, ...newPlaced.filter(p => !keptHeld.has(p.id))]
//       .reduce((acc,p)=>{ const ix=acc.findIndex(x=>x.id===p.id); if(ix>=0) acc[ix]=p; else acc.push(p); return acc; },[])
//       .sort((a,b)=>DAY_CODES.indexOf(a.day)-DAY_CODES.indexOf(b.day) || a.startMin-b.startMin);

//     setPlaced(merged);
//     setUnscheduled(uns);
//   };

//   const resetBoard = () => {
//     setPlaced([]);
//     const lessons = familyEps.map((e, i) => ({
//       id: `L-${e.key}`, endpointKey: e.key, label: e.label || `Family ${i+1}`, durationMin: 45,
//     }));
//     setUnscheduled(lessons);
//   };

//   const labelOf = (key) => endpoints.find(e => e.key === key)?.label || key;

//   return (
//     <div className="rounded-2xl border border-slate-200 p-4 bg-white">
//       <div className="flex items-center justify-between mb-3">
//         <h3 className="text-base font-semibold text-slate-900">Interactive Optimizer (drag & drop)</h3>
//         <div className="text-xs text-slate-500">
//           {loading ? `Loading matrix… ${progress.done}/${progress.total}` : err ? <span className="text-red-600">{err}</span> : `${endpoints.length} endpoints`}
//         </div>
//       </div>

//       {/* Controls */}
//       <div className="flex flex-wrap items-center gap-2 mb-4">
//         <button onClick={reoptimize} className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm">
//           Reoptimize around holds
//         </button>
//         <button onClick={resetBoard} className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-800 text-sm">
//           Reset board
//         </button>
//         {homeEp && <span className="text-xs text-slate-600">Start/End at <span className="font-medium">{homeEp.label}</span></span>}
//       </div>

//       <div className="grid grid-cols-12 gap-4">
//         {/* Unscheduled */}
//         <div className="col-span-3">
//           <div className="rounded-xl border border-slate-200">
//             <div className="px-3 py-2 text-sm font-medium text-slate-800 bg-slate-50 rounded-t-xl">Unscheduled</div>
//             <div className="p-2 space-y-2 min-h-[200px]">
//               {unscheduled.length === 0 ? (
//                 <div className="text-sm text-slate-500 px-2 py-4">All scheduled 🎉</div>
//               ) : unscheduled.map(u => (
//                 <div
//                   key={u.id}
//                   draggable
//                   onDragStart={(e)=>onDragStart(e,u.id)}
//                   className="rounded-lg border border-slate-200 p-2 bg-blue-100 cursor-grab active:cursor-grabbing"
//                   title="Drag to a day/time"
//                 >
//                   <div className="text-sm font-medium text-slate-900">{u.label}</div>
//                   <div className="text-xs text-slate-600">{u.durationMin} min</div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>

//         {/* Week grid */}
//         <div className="col-span-9">
//           <div className="grid grid-cols-8 gap-2">
//             <div className="text-xs text-slate-600" />
//             {DAY_CODES.map(d => (
//               <div key={d} className="text-xs font-medium text-slate-700 text-center">{d}</div>
//             ))}
//           </div>

//           <div className="flex gap-2">
//             {/* time gutter */}
//             <div className="w-16 relative">
//               <div className="relative h-[640px] border-l border-slate-200">
//                 {slots.map((m,i)=> (m % 60 === 0) && (
//                   <div key={i} className="absolute left-0 w-full text-[10px] text-slate-500 -translate-y-1/2"
//                        style={{ top: `${pct(m)}%` }}>
//                     {toHHMM(m)}
//                   </div>
//                 ))}
//               </div>
//             </div>

//             {/* 7 day columns */}
//             <div className="grid grid-cols-7 gap-2 flex-1">
//               {DAY_CODES.map((day) => {
//                 const a = availabilityByDay.get(day);
//                 const dayPlaced = placed.filter(p => p.day === day).sort((x,y)=>x.startMin-y.startMin);

//                 // Build drive segments (Home→first, between lessons, last→Home)
//                 const segments = [];
//                 if (a && dayPlaced.length) {
//                   const first = dayPlaced[0];
//                   const last  = dayPlaced[dayPlaced.length-1];
//                   if (homeEp) {
//                     const mStart = minutesBetween(minutesMap, homeEp.key, first.endpointKey);
//                     if (Number.isFinite(mStart)) {
//                       segments.push({
//                         minutes: mStart,
//                         topPct: (pct(a.start) + pct(first.startMin)) / 2,
//                         from: homeEp.label, to: first.label,
//                       });
//                     }
//                   }
//                   for (let i=0;i<dayPlaced.length-1;i++) {
//                     const A = dayPlaced[i], B = dayPlaced[i+1];
//                     const mAB = minutesBetween(minutesMap, A.endpointKey, B.endpointKey);
//                     if (Number.isFinite(mAB)) {
//                       segments.push({
//                         minutes: mAB,
//                         topPct: (pct(A.endMin) + pct(B.startMin)) / 2,
//                         from: A.label, to: B.label,
//                       });
//                     }
//                   }
//                   if (homeEp) {
//                     const mEnd = minutesBetween(minutesMap, last.endpointKey, homeEp.key);
//                     if (Number.isFinite(mEnd)) {
//                       segments.push({
//                         minutes: mEnd,
//                         topPct: (pct(last.endMin) + pct(a.end)) / 2,
//                         from: last.label, to: homeEp.label,
//                       });
//                     }
//                   }
//                 }

//                 const dayDriveTotal = segments.reduce((s, seg) => s + (Number.isFinite(seg.minutes) ? seg.minutes : 0), 0);

//                 return (
//                   <div key={day} className="relative h-[640px] bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
//                     {/* availability shading */}
//                     {a && (
//                       <div className="absolute left-0 right-0 bg-white/60"
//                            style={{ top: `${pct(a.start)}%`, height: `${pct(a.end)-pct(a.start)}%` }} />
//                     )}
//                     {/* hour lines */}
//                     {slots.map((m,i)=> (m%60===0) && (
//                       <div key={i} className="absolute left-0 right-0 h-px bg-blue-200" style={{ top: `${pct(m)}%` }} />
//                     ))}

//                     {/* drop zones */}
//                     {a && slots.filter(m=>m>=a.start && m+15<=a.end).map((m,i)=>(
//                       <div key={i}
//                            onDragOver={onDragOver}
//                            onDrop={(e)=>onDrop(e, day, m)}
//                            className="absolute left-0 right-0"
//                            style={{ top: `${pct(m)}%`, height: `${pct(m+15)-pct(m)}%` }}
//                       />
//                     ))}

//                     {/* placed lessons */}
//                     {dayPlaced.map(p => (
//                       <div key={p.id}
//                            draggable
//                            onDragStart={(e)=>onDragStart(e,p.id)}
//                            className={`absolute left-1 right-1 rounded-lg border px-2 py-1.5 shadow-sm ${p.held ? "bg-blue-600 text-white border-blue-700" : "bg-blue-200 text-slate-900 border-slate-300"} cursor-grab`}
//                            style={{ top: `${pct(p.startMin)}%`, height: `${pct(p.endMin)-pct(p.startMin)}%` }}
//                            title={`${p.label} • ${toHHMM(p.startMin)}–${toHHMM(p.endMin)} • ${p.held?"Held ":""}`}
//                       >
//                         <div className="text-[11px] font-semibold">{toHHMM(p.startMin)}–{toHHMM(p.endMin)}</div>
//                         <div className="text-xs truncate" onClick={()=>toggleHold(p.id)}>{p.label}</div>
//                         <div className="mt-1 flex items-center gap-2">
//                           {/* <label className="flex items-center gap-1 text-[11px]">
//                             <input type="checkbox" checked={p.held} onChange={()=>toggleHold(p.id)} />
//                             Hold
//                           </label> */}
//                           <button onClick={()=>removePlaced(p.id)} className="text-[11px] underline opacity-80 mt-5 hover:opacity-100">Remove</button>
//                         </div>
//                       </div>
//                     ))}

//                     {/* LIVE drive chips */}
//                     {segments.map((seg, i) => (
//                       <div key={`chip-${i}`}
//                            className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 border-green-500 border border-solid text-green-700 text-[10px] px-2 py-0.5 rounded-full flex text-center shadow"
//                            style={{ top: `${seg.topPct}%` }}
//                            title={`Drive ${seg.minutes}m (${seg.from} → ${seg.to})`}
//                       >
//                         Drive {seg.minutes}m
//                       </div>
//                     ))}

//                     {/* day total */}
//                     {a && (
//                       <div className="absolute top-1 right-1 bg-slate-900 text-white text-[10px] px-2 py-0.5 rounded-full shadow"
//                            title={`Total drive time (including Home legs)`}
//                       >
//                         {dayDriveTotal}m
//                       </div>
//                     )}
//                   </div>
//                 );
//               })}
//             </div>
//           </div>
//         </div>
//       </div>

//       <div className="mt-3 text-xs text-slate-500">
//         Drive chips update automatically as you place/move lessons. They include Home→first and last→Home legs.
//       </div>
//     </div>
//   );
// }

// /* ---------------------- HOLDS PANEL ---------------------- */

// function HoldsPanel({ semesterId }) {
//   const [rows, setRows] = useState([]);
//   useEffect(() => {
//     setRows(DEMO_HOLDS.map((h, i) => ({ id: `hold-${i}`, ...h })));
//   }, [semesterId]);

//   const onConfirm = (id) => {
//     setRows((r) => r.filter((x) => x.id !== id));
//     alert("Confirmed (demo)");
//   };
//   const onCancel = (id) => setRows((r) => r.filter((x) => x.id !== id));

//   return (
//     <div className="rounded-2xl border border-slate-200 p-4 bg-white">
//       <div className="flex items-center justify-between mb-3">
//         <h3 className="text-base font-semibold text-slate-900">Holds queue</h3>
//         <button
//           onClick={() => setRows(DEMO_HOLDS.map((h, i) => ({ id: `hold-${i}`, ...h })))}
//           className="text-sm text-blue-600 hover:underline"
//         >
//           Refresh
//         </button>
//       </div>

//       {rows.length === 0 ? (
//         <div className="text-sm text-slate-500">No active holds.</div>
//       ) : (
//         <div className="divide-y divide-slate-100">
//           {rows.map((h) => {
//             const mins = Math.max(
//               0,
//               Math.floor((new Date(h.expiresAt).getTime() - Date.now()) / 60000)
//             );
//             return (
//               <div key={h.id} className="py-3 flex items-center justify-between">
//                 <div className="min-w-0">
//                   <div className="font-medium text-slate-900 truncate">{h.familyName}</div>
//                   <div className="text-sm text-slate-600 truncate">
//                     {h.weekday} {h.start} · {h.durationMin}m · {h.teacherName}
//                   </div>
//                 </div>
//                 <div className="flex items-center gap-3">
//                   <Tag tone={mins < 30 ? "red" : "amber"}>exp {mins}m</Tag>
//                   <button
//                     onClick={() => onConfirm(h.id)}
//                     className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm"
//                   >
//                     Confirm
//                   </button>
//                   <button
//                     onClick={() => onCancel(h.id)}
//                     className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-800 text-sm"
//                   >
//                     Cancel
//                   </button>
//                 </div>
//               </div>
//             );
//           })}
//         </div>
//       )}
//     </div>
//   );
// }

// /* ---------------------- MAIN ---------------------- */

// export default function ManagerDashboardWeekColumns() {
//   const { semesters } = useSemesters();
//   const { teachers } = useTeachers();

//   const [filters, setFilters] = useState({ semesterId: "", teacherId: "" });

//   // Your DB teacher id for drive endpoints
//   const [teacherDbId, setTeacherDbId] = useState("f5ed52c429b3a");
//   // HH:MM bucket to request drive minutes for (must match how you warmed cache)
//   const [bucketHHMM, setBucketHHMM] = useState("16:00");

//   const [week, setWeek] = useState([]);
//   const [metrics, setMetrics] = useState({
//     lessons: 0,
//     totalIdleMin: 0,
//     totalDriveMin: 0,
//     avgDriveMin: 0,
//     utilizationPct: 0,
//   });

//   // auto-select first items
//   useEffect(() => {
//     setFilters((f) => ({ ...f, semesterId: f.semesterId || semesters[0]?.id || "" }));
//   }, [semesters]);
//   useEffect(() => {
//     setFilters((f) => ({ ...f, teacherId: f.teacherId || teachers[0]?.id || "" }));
//   }, [teachers]);

//   // compute full week in memory (demo)
//   useEffect(() => {
//     const teacher = teachers.find((t) => t.id === filters.teacherId);
//     if (!teacher || !filters.semesterId) {
//       setWeek([]);
//       return;
//     }
//     const days = WEEKDAYS.map((d) => {
//       const x = getTeacherDay({ teacherName: teacher.name, weekday: d });
//       return (
//         x || {
//           teacher: { name: teacher.name },
//           weekday: d,
//           timeline: [],
//           metrics: { lessons: 0, totalIdleMin: 0, totalDriveMin: 0, avgDriveMin: 0, utilizationPct: 0 },
//         }
//       );
//     });
//     setWeek(days);

//     // aggregate metrics (demo)
//     const sumLessons = days.reduce((s, d) => s + (d.metrics.lessons || 0), 0);
//     const sumIdle = days.reduce((s, d) => s + (d.metrics.totalIdleMin || 0), 0);
//     const sumDrive = days.reduce((s, d) => s + (d.metrics.totalDriveMin || 0), 0);
//     const avgDrive = days.length ? Math.round(sumDrive / days.length) : 0;
//     const avgUtil = days.length
//       ? Math.round(days.reduce((s, d) => s + (d.metrics.utilizationPct || 0), 0) / days.length)
//       : 0;

//     setMetrics({
//       lessons: sumLessons,
//       totalIdleMin: sumIdle,
//       totalDriveMin: sumDrive,
//       avgDriveMin: avgDrive,
//       utilizationPct: avgUtil,
//     });
//   }, [teachers, filters]);

//   const teacherName = teachers.find((t) => t.id === filters.teacherId)?.name || "—";

//   return (
//     <div className="max-w-7xl mx-auto p-6 space-y-6">
//       <div className="flex items-center justify-between">
//         <h1 className="text-2xl md:text-3xl font-semibold text-slate-900">Manager Dashboard (Calendar + Drive Matrix)</h1>
//       </div>

//       <Toolbar semesters={semesters} teachers={teachers} value={filters} onChange={setFilters} />

//       {/* Week metrics */}
//       <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
//         <StatCard label="Teacher" value={teacherName} />
//         <StatCard label="Lessons (week)" value={metrics.lessons} />
//         <StatCard label="Idle (week)" value={`${metrics.totalIdleMin} m`} />
//         <StatCard label="Drive (week)" value={`${metrics.totalDriveMin} m`} sub={`avg/day ${metrics.avgDriveMin} m`} />
//         <StatCard label="Avg Utilization" value={`${metrics.utilizationPct}%`} />
//       </div>

//       {/* Calendar grid (demo) */}
//       <WeekCalendar week={week} />

//       {/* Drive Matrix controls */}
//       <div className="rounded-2xl border border-slate-200 p-4 bg-white">
//         <div className="flex flex-col md:flex-row md:items-center gap-3">
//           <div className="flex items-center gap-2">
//             <div className="text-sm text-slate-700 font-medium">Teacher DB Id:</div>
//             <input
//               value={teacherDbId}
//               onChange={(e) => setTeacherDbId(e.target.value)}
//               className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm w-72"
//               placeholder="paste teacher _id for drive matrix"
//             />
//           </div>
//           <div className="flex items-center gap-2">
//             <div className="text-sm text-slate-700 font-medium">Bucket (HH:MM):</div>
//             <input
//               value={bucketHHMM}
//               onChange={(e) => setBucketHHMM(e.target.value)}
//               className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm w-28"
//               placeholder="16:00"
//             />
//           </div>
//           <div className="text-xs text-slate-500">We’ll fetch endpoints, then minutes for every pair, and render a heatmap.</div>
//         </div>
//       </div>

//       {/* Drive Matrix panel */}
//       <DriveMatrixPanel teacherDbId={teacherDbId} bucketHHMM={bucketHHMM} />

//       {/* Holds panel (demo) */}
//       <HoldsPanel semesterId={filters.semesterId} />

//       {/* Interactive Optimizer */}
//       <InteractiveOptimizer
//         teacher={teachers.find(t => t.id === filters.teacherId)}
//         teacherDbId={teacherDbId}
//         bucketHHMM={bucketHHMM}
//       />

//     </div>
//   );
// }
